import { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';

const VIDEO_URL = 'https://customer-assets.emergentagent.com/job_6551900b-6eea-49b4-a7ed-baa10c533f6e/artifacts/fk1eq9f3_flyoverAravellGolf.mp4';
// Fallback image while video loads
const POSTER_IMAGE = 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?q=80&w=1920&auto=format&fit=crop';

export const VideoHero = ({ title, subtitle }) => {
  const videoRef = useRef(null);
  const { language } = useLanguage();
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // When video can play, show it
    const handleCanPlay = () => {
      setVideoLoaded(true);
    };

    // When video metadata is loaded, set to start at last 10 seconds
    const handleLoadedMetadata = () => {
      const duration = video.duration;
      if (duration > 10) {
        video.currentTime = duration - 10;
      }
      video.play().catch(() => {
        // Autoplay might be blocked, that's okay
        setVideoLoaded(true);
      });
    };

    // Loop the last 10 seconds
    const handleTimeUpdate = () => {
      const duration = video.duration;
      // If we're at the very end, jump back to last 10 seconds
      if (video.currentTime >= duration - 0.1) {
        video.currentTime = duration - 10;
      }
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', () => {
      const duration = video.duration;
      if (duration > 10) {
        video.currentTime = duration - 10;
        video.play();
      }
    });

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, []);

  return (
    <section className="relative h-[60vh] md:h-[70vh] lg:h-[80vh] overflow-hidden" data-testid="video-hero">
      {/* Fallback Image (shows while video loads) */}
      <div 
        className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}
        style={{ backgroundImage: `url(${POSTER_IMAGE})` }}
      />

      {/* Video Background */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
        poster={POSTER_IMAGE}
      >
        <source src={VIDEO_URL} type="video/mp4" />
      </video>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/30" />

      {/* Content */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
              UGOLF ARAVELL Andorra
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white font-medium tracking-tight mb-6">
              {title}
            </h1>
            {subtitle && (
              <p className="text-white/80 text-base sm:text-lg lg:text-xl max-w-2xl mx-auto">
                {subtitle}
              </p>
            )}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-white/60 text-xs tracking-widest uppercase">
            {language === 'ca' ? 'Descobreix' : language === 'es' ? 'Descubre' : language === 'fr' ? 'Découvrir' : 'Discover'}
          </span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center pt-2"
          >
            <div className="w-1.5 h-1.5 bg-white rounded-full" />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default VideoHero;
